"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Alert = void 0;
const uuid_1 = require("uuid");
const AlertMapper_1 = require("../mappers/AlertMapper");
const MessageUtils_1 = require("../mappers/MessageUtils");
const models_1 = require("../models");
const Observable_1 = require("../socket/Observable");
const utils_1 = require("../utils");
const DomainEvents_1 = require("../utils/DomainEvents");
class Alert {
    constructor(organizationUser, httpAdapter, userDirectory, hhnStorage, socketAdapter) {
        this.removeListeners = [];
        this.changeListeners = [];
        // Tracks how many raw items the backend has actually returned so far, independent
        // of `sentAcknowledgement.data.length` — mergeUniqueAlertData can dedupe an
        // overlapping page down to zero new rows, and offsetting by the deduped array
        // length then permanently re-requests an already-seen window (offset never advances).
        this.sentAcknowledgementOffset = 0;
        this.organizationUser = organizationUser;
        this.httpAdapter = httpAdapter;
        this.userDirectory = userDirectory;
        this.hhnStorage = hhnStorage;
        this.socketAdapter = socketAdapter;
        this.alerts = {
            priority: { data: [], limit: 0, total: 0, hasNext: false },
            acknowledgement: { data: [], limit: 0, total: 0, hasNext: false },
            sentAcknowledgement: { data: [], limit: 0, total: 0, hasNext: false },
            referral: { data: [], limit: 0, total: 0, hasNext: false },
        };
    }
    init() {
        if (this.removeListeners && this.removeListeners.length > 0) {
            throw new Error("Panic: Listeners should have been destoryed during unmount!!!");
        }
        this.removeListeners.push(this.removeAlertListener(() => {
            this.notifyChange();
        }));
        this.removeListeners.push(this.removeAcknowledgementAlertListener(() => {
            this.notifyChange();
        }));
        this.removeListeners.push(this.newAlertListener(() => {
            this.notifyChange();
        }));
        this.removeListeners.push(this.addAcknowledgementListener(() => {
            this.notifyChange();
        }));
        this.removeListeners.push(this.deleteAlertListener(() => {
            this.notifyChange();
        }));
        this.removeListeners.push(this.organizationUser
            .getNetworkManager()
            .registerSocketAuthSuccessListener((_lastHeartbeatReceivedAt, abortContext) => __awaiter(this, void 0, void 0, function* () {
            yield this.syncAlerts(utils_1.HHNConstants.HEADER_SOURCE_SYNC, abortContext);
        })));
    }
    registerChangeListener(cb) {
        const id = (0, uuid_1.v4)();
        this.changeListeners.push({ id, cb });
        return () => {
            this.changeListeners = this.changeListeners.filter(listner => listner.id !== id);
        };
    }
    notifyChange() {
        this.changeListeners.forEach(l => l.cb(this.alerts));
    }
    destroy() {
        this.removeAllListeners();
    }
    syncAlerts(source, abortContext) {
        return __awaiter(this, void 0, void 0, function* () {
            const newAlertsResult = yield this.fetchAlerts(this.organizationUser.getOrganization().organizationId, source, abortContext);
            if (!newAlertsResult.isAborted) {
                this.notifyChange();
                Observable_1.Observable.getInstance().notify((0, DomainEvents_1.getEventKey)(DomainEvents_1.DomainEvents.ORG_COUNTERS, this.organizationUser.getOrganization().organizationId), this.hhnStorage.orgStorage.getCounters());
            }
        });
    }
    removeAllListeners() {
        this.removeListeners.forEach(removeListener => removeListener());
        this.removeListeners = [];
    }
    addAcknowledgementListener(cb) {
        return Observable_1.Observable.getInstance().subscribe((0, DomainEvents_1.getEventKey)(DomainEvents_1.DomainEvents.ALERT_UPDATE, this.organizationUser.getOrganization().organizationId), (message) => this.updateAcknowledgementMessage(cb, message));
    }
    newAlertListener(cb) {
        return Observable_1.Observable.getInstance().subscribe((0, DomainEvents_1.getEventKey)(DomainEvents_1.DomainEvents.NEW_ALERT, this.organizationUser.getOrganization().organizationId), (data) => this.updateAlertList(cb, data));
    }
    removeAlertListener(cb) {
        return Observable_1.Observable.getInstance().subscribe((0, DomainEvents_1.getEventKey)(DomainEvents_1.DomainEvents.ALERT_REMOVE, this.organizationUser.getOrganization().organizationId), (data) => this.processAlertRemove(data, cb));
    }
    removeAcknowledgementAlertListener(cb) {
        return Observable_1.Observable.getInstance().subscribe((0, DomainEvents_1.getEventKey)(DomainEvents_1.DomainEvents.ALERT_REMOVE_ACKNOWLEDGEMENT, this.organizationUser.getOrganization().organizationId), (data) => this.processAcknowledgementAlertRemove(data, cb));
    }
    deleteAlertListener(cb) {
        return Observable_1.Observable.getInstance().subscribe((0, DomainEvents_1.getEventKey)(DomainEvents_1.DomainEvents.ALERT_DELETE, this.organizationUser.getOrganization().organizationId), (data) => {
            this.processAlertDelete(data, cb);
        });
    }
    setCounter() {
        var _a, _b, _c, _d;
        const priorityCount = (_a = this.alerts.priority.total) !== null && _a !== void 0 ? _a : 0;
        const acknowledgeCount = (_b = this.alerts.acknowledgement.total) !== null && _b !== void 0 ? _b : 0;
        const sentAcknowledgementCount = (_c = this.alerts.sentAcknowledgement.total) !== null && _c !== void 0 ? _c : 0;
        const referral = (_d = this.alerts.referral.total) !== null && _d !== void 0 ? _d : 0;
        this.hhnStorage.orgStorage.setAlertsCount(priorityCount, acknowledgeCount, sentAcknowledgementCount, referral);
    }
    fetchAlerts(organizationId, source, abortContext) {
        return __awaiter(this, void 0, void 0, function* () {
            const alertResult = yield this.httpAdapter
                .getAlertAdapter()
                .getAllAlerts(organizationId, source, abortContext);
            if (!alertResult.isAborted) {
                this.alerts = (0, AlertMapper_1.alertMapper)(alertResult.data, this.userDirectory, this.organizationUser, this.httpAdapter, this.socketAdapter);
                this.sentAcknowledgementOffset =
                    this.alerts.sentAcknowledgement.data.length;
                this.setCounter();
                return (0, utils_1.CompletedResult)(this.alerts);
            }
            return utils_1.AbortedResult;
        });
    }
    getAlerts() {
        return this.alerts;
    }
    // Paginated fetches can return an alert already present in `existing` (e.g. the
    // initial getAllAlerts summary page overlapping with a later paginated fetch).
    // FlashList keys rows by alertId, so an un-deduped append renders duplicate-keyed
    // rows as blank while still counting them toward scroll height.
    mergeUniqueAlertData(existing, incoming) {
        const seenAlertIds = new Set(existing.map(item => item.alertId));
        const uniqueIncoming = [];
        for (const item of incoming) {
            if (!seenAlertIds.has(item.alertId)) {
                seenAlertIds.add(item.alertId);
                uniqueIncoming.push(item);
            }
        }
        return [...existing, ...uniqueIncoming];
    }
    fetchAcknowledgements() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.alerts.acknowledgement.hasNext) {
                // Offset must exclude READ items — the backend drops resolved alerts from
                // its ordering, so counting them here skips past real unresolved ones.
                const pendingResponse = yield this.httpAdapter
                    .getAlertAdapter()
                    .fetchAcknowledgements({
                    limit: utils_1.HHNConstants.PAGINATION_LIMIT,
                    total: this.alerts.acknowledgement.data.filter(data => data.alertStatus === models_1.ALERT_STATUS.UNREAD).length,
                });
                const pendingMappedData = (0, AlertMapper_1.alertTypeMapper)(pendingResponse, this.userDirectory, this.organizationUser, this.httpAdapter, this.socketAdapter);
                if (this.alerts.acknowledgement.data.length > 0) {
                    this.alerts.acknowledgement = Object.assign(Object.assign({}, pendingMappedData), { data: this.mergeUniqueAlertData(this.alerts.acknowledgement.data, pendingMappedData.data) });
                }
                else {
                    this.alerts.acknowledgement = pendingMappedData;
                }
                this.notifyChange();
            }
        });
    }
    fetchPriorityAlerts() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.alerts.priority.hasNext) {
                // Offset must exclude READ items — the backend drops resolved alerts from
                // its ordering, so counting them here skips past real unresolved ones.
                const requestOffset = this.alerts.priority.data.filter(data => data.alertStatus === models_1.ALERT_STATUS.UNREAD).length;
                const priorityAlertsResponse = yield this.httpAdapter
                    .getAlertAdapter()
                    .fetchPriorityAlerts({
                    limit: utils_1.HHNConstants.PAGINATION_LIMIT,
                    total: requestOffset,
                });
                const priorityAlertsMappedData = (0, AlertMapper_1.alertTypeMapper)(priorityAlertsResponse, this.userDirectory, this.organizationUser, this.httpAdapter, this.socketAdapter);
                if (this.alerts.priority.data.length > 0) {
                    this.alerts.priority = Object.assign(Object.assign({}, priorityAlertsMappedData), { data: this.mergeUniqueAlertData(this.alerts.priority.data, priorityAlertsMappedData.data) });
                }
                else {
                    this.alerts.priority = priorityAlertsMappedData;
                }
                this.notifyChange();
            }
        });
    }
    fetchSentAcknowledgement() {
        return __awaiter(this, void 0, void 0, function* () {
            const sentResponse = yield this.httpAdapter
                .getAlertAdapter()
                .fetchSentAcknowledgements({
                limit: utils_1.HHNConstants.PAGINATION_LIMIT,
                total: this.sentAcknowledgementOffset,
            });
            const sentMappedData = (0, AlertMapper_1.alertTypeMapper)(sentResponse, this.userDirectory, this.organizationUser, this.httpAdapter, this.socketAdapter);
            this.sentAcknowledgementOffset += sentMappedData.data.length;
            if (this.alerts.sentAcknowledgement.data.length > 0) {
                this.alerts.sentAcknowledgement = Object.assign(Object.assign({}, sentMappedData), { data: this.mergeUniqueAlertData(this.alerts.sentAcknowledgement.data, sentMappedData.data) });
            }
            else {
                this.alerts.sentAcknowledgement = sentMappedData;
            }
            return this.alerts.sentAcknowledgement;
        });
    }
    updateAlertList(callback, data) {
        const alertTypeItemData = (0, AlertMapper_1.alertTypeItemMapper)(data, this.userDirectory, this.organizationUser, this.httpAdapter, this.socketAdapter);
        switch (alertTypeItemData.type) {
            case models_1.ALERT_TYPE.PRIORITY:
                this.alerts = Object.assign(Object.assign({}, this.alerts), { priority: Object.assign(Object.assign({}, this.alerts.priority), { data: [alertTypeItemData, ...this.alerts.priority.data] }) });
                break;
            case models_1.ALERT_TYPE.ACKNOWLEDGE:
                this.alerts = Object.assign(Object.assign({}, this.alerts), { acknowledgement: Object.assign(Object.assign({}, this.alerts.acknowledgement), { data: [alertTypeItemData, ...this.alerts.acknowledgement.data] }) });
                break;
            case models_1.ALERT_TYPE.REFERRAL:
                this.alerts = Object.assign(Object.assign({}, this.alerts), { referral: Object.assign(Object.assign({}, this.alerts.referral), { data: [alertTypeItemData, ...this.alerts.referral.data] }) });
                break;
        }
        callback(this.alerts);
        this.hhnStorage.orgStorage.updateAlertCountByType(alertTypeItemData.type, true);
    }
    processAlertRemove(alertRemoveData, callback) {
        switch (alertRemoveData.alertType) {
            case models_1.ALERT_TYPE.PRIORITY:
                // eslint-disable-next-line no-case-declarations
                //update the priority data and set the status of that priority to read
                this.alerts.priority.data.map(data => {
                    if (data.from === alertRemoveData.chatId) {
                        data.alertStatus = models_1.ALERT_STATUS.READ;
                    }
                    return data;
                });
                break;
            case models_1.ALERT_TYPE.REFERRAL:
                // eslint-disable-next-line no-case-declarations
                const updatedReferralData = this.alerts.referral.data.filter(data => data.from !== alertRemoveData.chatId);
                this.alerts = Object.assign(Object.assign({}, this.alerts), { referral: Object.assign(Object.assign({}, this.alerts.referral), { data: updatedReferralData }) });
                break;
        }
        callback(this.alerts);
        // This condition is to avoid decrementing ack alert count - ack alert count is decremented in processAcknowledgementAlertRemove when v2-alert-remove is received
        if (alertRemoveData.alertType === models_1.ALERT_TYPE.ACKNOWLEDGE) {
            return;
        }
        this.hhnStorage.orgStorage.updateAlertCountByType(alertRemoveData.alertType, false, alertRemoveData.numberOfAlertsRemoved);
    }
    processAcknowledgementAlertRemove(alertRemoveData, callback) {
        switch (alertRemoveData.alertType) {
            case models_1.ALERT_TYPE.ACKNOWLEDGE:
                this.alerts = Object.assign(Object.assign({}, this.alerts), { acknowledgement: Object.assign(Object.assign({}, this.alerts.acknowledgement), { data: this.alerts.acknowledgement.data.map(data => data.alertId === alertRemoveData.alertId
                            ? Object.assign(Object.assign({}, data), { alertStatus: models_1.ALERT_STATUS.READ }) : data) }) });
                callback(this.alerts);
                break;
        }
        this.hhnStorage.orgStorage.updateAlertCountByType(alertRemoveData.alertType, false);
    }
    processAlertDelete(chatDeleteData, callback) {
        const chatId = chatDeleteData.from;
        // Remove all alerts related to the deleted chat from all alert types
        this.alerts = {
            priority: Object.assign(Object.assign({}, this.alerts.priority), { data: this.alerts.priority.data.filter(data => data.from !== chatId) }),
            acknowledgement: Object.assign(Object.assign({}, this.alerts.acknowledgement), { data: this.alerts.acknowledgement.data.filter(data => data.from !== chatId) }),
            sentAcknowledgement: Object.assign(Object.assign({}, this.alerts.sentAcknowledgement), { data: this.alerts.sentAcknowledgement.data.filter(data => data.from !== chatId) }),
            referral: Object.assign({}, this.alerts.referral),
        };
        callback(this.alerts);
    }
    updateAcknowledgementMessage(callback, message) {
        const updatedMessage = (0, MessageUtils_1.makeMessage)(message, this.userDirectory, this.organizationUser, this.httpAdapter, this.socketAdapter);
        if (updatedMessage) {
            const updatedAlerts = this.alerts.acknowledgement.data.filter(item => {
                var _a;
                if (item.messageId === (updatedMessage === null || updatedMessage === void 0 ? void 0 : updatedMessage.getMessage().id)) {
                    let type = models_1.ALERT_TYPE[updatedMessage === null || updatedMessage === void 0 ? void 0 : updatedMessage.getMessage().type];
                    (_a = updatedMessage === null || updatedMessage === void 0 ? void 0 : updatedMessage.getMessage().visibleTo) === null || _a === void 0 ? void 0 : _a.map(visibleTo => {
                        if (item.user === visibleTo.id) {
                            type =
                                visibleTo.isAcknowledged === 1
                                    ? models_1.ALERT_TYPE.ACKNOWLEDGED
                                    : models_1.ALERT_TYPE.ACKNOWLEDGE;
                        }
                    });
                    item.type = type;
                    item.message = updatedMessage;
                }
                return item;
            });
            this.alerts = Object.assign(Object.assign({}, this.alerts), { acknowledgement: Object.assign(Object.assign({}, this.alerts.acknowledgement), { data: [...updatedAlerts] }) });
            callback(this.alerts);
        }
    }
    handleReferralAlerts(data) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.httpAdapter.getAlertAdapter().handleReferrals(data);
        });
    }
}
exports.Alert = Alert;
